import React, {ChangeEvent, useEffect, useState} from 'react';
import {
    Box,
    Button,
    FormControl,
    FormControlLabel,
    FormLabel,
    Grid,
    Radio,
    RadioGroup,
    ThemeProvider
} from "@mui/material";
import {SETable} from "../../../../components/SETable";
import {RiFilter3Line, RiFilterOffLine, RiSearchLine} from "react-icons/ri";
import {SEText} from "../../../../components/SEText";
import {theme} from "../../../../mui-theme";
import {tableColumns} from "./tableColumns";
import {useBodyFilter, usePaginationFilters} from "../../../../hooks";
import {AcessosService} from "../AcessosService";
import {useQuery} from "react-query";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import {makeStyles} from "@material-ui/core/styles";
import {SEButtonExport} from "../../../../components/SEButtonExport";
import {SEDateRangePicker} from "../../../../components/SEDateRangePicker";
import SEFilterSelect from "../../../../components/SEFilterSelect";
import {
    filterParamsByUrlField,
    removeEmptyProperties,
    sortArrayByDirection,
    sortArrayByProp
} from "../../../../utils/utils";
import {useHistory, useLocation} from "react-router-dom";
import moment from "moment";
import {formatDateToBR, formatHoursToBRWithoutSeconds} from "../../../../../utils/time";
import {cnpjMask, cpfMask} from "../../../../../utils/mask";
import useExcelExport from "../../../../hooks/useExcelExport";

const useStyles = makeStyles({
    textField: {
        '& .MuiOutlinedInput-root': {
            height: '40px',
            '& input': {
                border: 'none', // Remove a borda do TextField
            }
        },
    },
});


type FilterOptionsProps = {
    cidades?: any,
    ufs?: any,
    cnaes?: any,
    segmentos?: any,
    portes?: any,
    temposDeAcesso?: any,
    meses?: any,
    anos?: any,
    usuariosQue?: any,
}

const filtersToApply = [
    {
        urlParam: 'ufs',
        fieldToCompare: 'sigla',
        returnAsArray: true
    },

    {
        urlParam: 'cidades',
        fieldToCompare: 'nome',
        returnAsArray: true
    },
    {
        urlParam: 'segmentos',
        fieldToCompare: 'nome',
        returnAsArray: true
    },
    {
        urlParam: 'tempoAcesso',
        fieldToSearch: 'temposDeAcesso'
    },
    {
        urlParam: 'ano',
        fieldToSearch: 'anos'
    },
    {
        urlParam: 'mes',
        fieldToSearch: 'meses'
    },
    {
        urlParam: 'usuariosQue',
        fieldToSearch: 'usuariosQue'
    },
    {
        urlParam: 'dataInicial',
    },
    {
        urlParam: 'dataFinal',
    },
    {
        urlParam: 'isCPF',
    },
    {
        urlParam: 'pessoaJuridica',
    },
];


const fetchData = async (urlParams: any, body: any) => {
    try {
        const bodyParams = removeEmptyProperties(body);
        return await AcessosService.findAll(urlParams, bodyParams);
    } catch (error) {
        console.error('Ocorreu um erro:', error);
        throw new Error('Ocorreu um erro ao buscar os dados.');
    }
};


const Report = () => {

    const history = useHistory();
    const location = useLocation();

    const [paginationFilters, setPaginationFilters, resetPaginationFilters] = usePaginationFilters(history);
    const [bodyFilters, setBodyFilters, resetFilters] = useBodyFilter();
    const [filterOptions, setFilterOptions] = useState<FilterOptionsProps>({});
    const [filteredCities, setFilteredCities] = useState([]);
    const [tipoUsuarioRadio, setTipoUsuarioRadio] = useState('todos');
    const [dateRangeValue, setDateRangeValue] = useState({startDate: '', endDate: ''})


    const fetchDataExcel = async () => {
        const bodyParams = removeEmptyProperties(bodyFilters);
        const pageFilter = { ...paginationFilters, page: 0, linesPerPage: 1000 };
        const response = await AcessosService.findAll(pageFilter, bodyParams);

        return response?.content?.map((xls: any) => ({
            DATA: moment(xls?.data).format('DD/MM/YYYY'),
            HORA: formatHoursToBRWithoutSeconds(xls?.fim),
            NOME: xls?.administrador?.nome,
            CPF: cpfMask(xls?.cpf),
            CNPJ: cnpjMask(xls?.empreendedor?.cnpj),
            UF: xls?.uf?.sigla,
            CIDADE: xls?.cidade?.nome,
            TEMPO_ACESSO: xls?.tempoLogado,
        }));
    };

    const { executeDownload, downloading } = useExcelExport({
        handleDataCallback: fetchDataExcel,
        filename: `Exportacao_Acessos_${formatDateToBR(new Date())}`,
        colorLoading: '#3498db',
        isCsv: true,
    });

    const {data, isLoading} = useQuery(
        ['reportData', paginationFilters, bodyFilters],
        () => fetchData(paginationFilters, bodyFilters),
        {refetchOnWindowFocus: false} // Evitar refetch ao focar na janela
    );

    useEffect(() => {
        const fetchOptionData = async () => {
            const response = await AcessosService.getOptions();
            setFilterOptions(response);
        }
        fetchOptionData()
    }, []);

    useEffect(() => {
        console.log('bodyFilters', bodyFilters)
        if (bodyFilters?.dataInicial && bodyFilters?.dataFinal) {
            setDateRangeValue({startDate: bodyFilters.dataInicial, endDate: bodyFilters?.dataFinal})
        }
        if (bodyFilters?.isCPF || bodyFilters?.pessoaJuridica) {
            if (bodyFilters.isCPF === "true" && bodyFilters?.pessoaJuridica === "true") {
                setTipoUsuarioRadio('todos')
            }
            if (bodyFilters.isCPF === "false" && bodyFilters?.pessoaJuridica === "true") {
                setTipoUsuarioRadio('cnpj')
            }
            if (bodyFilters.isCPF === "true" && bodyFilters?.pessoaJuridica === "false") {
                setTipoUsuarioRadio('cpf')
            }
        }
    }, [bodyFilters]);

    useEffect(() => {
        const loadUrlParams = () => {
            const urlSearchParams = new URLSearchParams(location.search);
            if (filtersToApply) {
                filtersToApply.forEach(filter => {
                    filterParamsByUrlField({
                        setBodyFilters,
                        urlSearchParams,
                        filterOptions,
                        ...filter
                    });
                });
            }
        }
        if (filterOptions) {
            loadUrlParams()
        }
        // eslint-disable-next-line
    }, [filterOptions]);

    const handleLinesPerPage = (linesPerPage: string) => {
        setPaginationFilters({linesPerPage, page: 0})
    }

    const handleChangeOrder = (order: {
        orderBy: string;
        direction: string;
    }) => {
        setPaginationFilters(order)
    }

    const handlePageChange = (value: number) => {
        setPaginationFilters({page: value});
    }
    const handleSearchChange = (event: any) => {
        const value = event.target.value;
        setBodyFilters({
            property: 'oqueVoceProcura',
            value
        });
    }

    const handleRangeDateChange = (value: any) => {
        setBodyFilters({
            property: 'dataInicial',
            value: value.startDate,
            putOnUrl: true,
        });

        setBodyFilters({
            property: 'dataFinal',
            value: value.endDate,
            putOnUrl: true
        });
        setDateRangeValue(value)
    }

    const handleUFChange = (uf: any) => {
        setBodyFilters({
            displayField: 'sigla',
            putOnUrl: true,
            isArrayValue: true,
            property: 'ufs',
            value: uf,
        });
        const data = filterOptions?.cidades?.filter((c: any) => c.uf.id === uf.id);
        data && setFilteredCities(data);
    }

    const handleResetFilters = () => {
        setDateRangeValue({startDate: '', endDate: ''})
        setTipoUsuarioRadio('todos');
        resetFilters();
        resetPaginationFilters();
    }

    const handleChangeRadio = (event: ChangeEvent<HTMLInputElement>) => {
        const selectedValue = event.target.value;
        setTipoUsuarioRadio(selectedValue);
        switch (selectedValue) {
            case 'todos':
                setBodyFilters({
                    property: 'isCPF',
                    value: true,
                    putOnUrl: true,
                });
                setBodyFilters({
                    property: 'pessoaJuridica',
                    value: true,
                    putOnUrl: true,
                });
                break;
            case 'cpf':
                setBodyFilters({
                    property: 'isCPF',
                    value: true,
                    putOnUrl: true,
                });
                setBodyFilters({
                    property: 'pessoaJuridica',
                    value: false,
                    putOnUrl: true,
                });
                break;
            case 'cnpj':
                setBodyFilters({
                    property: 'isCPF',
                    value: false,
                    putOnUrl: true,
                });
                setBodyFilters({
                    property: 'pessoaJuridica',
                    value: true,
                    putOnUrl: true,
                });
                break;
            default:
                break;
        }
    };




    const classes = useStyles();

    return (
        <React.Fragment>
            <Grid item>
                <Grid item flexDirection='row' display="flex" alignItems="center">
                    <Grid item flexDirection='row' display="flex" alignItems="center" mr={3}>
                        <RiFilter3Line/>
                        <SEText as="p" font="Mulish" weight="bold">Filtros</SEText>
                    </Grid>
                </Grid>
                <ThemeProvider theme={theme}>
                    <Grid container spacing={2} my={2}>
                        <Grid item lg={3} md={3}>
                            <SEFilterSelect
                                id={"tipodiagnostico"}
                                label='Tipo do diagnóstico'
                                value={bodyFilters?.segmentos?.[0]}
                                defaultOption={'Todas'}
                                options={sortArrayByProp(filterOptions?.segmentos, 'nome')}
                                displayField='nome'
                                onChange={(value) => {
                                    setBodyFilters({
                                        displayField: 'nome',
                                        putOnUrl: true,
                                        isArrayValue: true,
                                        property: 'segmentos',
                                        value: value,
                                    });
                                }}
                            />
                        </Grid>

                        <Grid item lg={2} md={2}>
                            <SEFilterSelect
                                defaultOption={'Todas'}
                                id={"uf"}
                                label='UF'
                                value={bodyFilters?.ufs?.[0]}
                                options={sortArrayByProp(filterOptions?.ufs, 'sigla')}
                                displayField='sigla'
                                onChange={handleUFChange}
                            />
                        </Grid>


                        <Grid item lg={2} md={2}>
                            <SEFilterSelect
                                defaultOption={'Todas'}
                                id="cidade"
                                label='Cidade'
                                value={bodyFilters?.cidades?.[0]}
                                options={sortArrayByProp(filteredCities ?? filterOptions?.cidades, 'nome')}
                                displayField='nome'
                                onChange={(value) => {
                                    setBodyFilters({
                                        displayField: 'nome',
                                        putOnUrl: true,
                                        isArrayValue: true,
                                        property: 'cidades',
                                        value: value,
                                    });
                                }}
                            />
                        </Grid>

                        <Grid item lg={2} md={2}>
                            <FormControl fullWidth>
                                <SEDateRangePicker value={dateRangeValue} onChange={handleRangeDateChange}/>
                            </FormControl>
                        </Grid>

                        <Grid item lg={3} md={3}>
                            <SEFilterSelect
                                customLabel={(value) => value.replaceAll('_', ' ')}
                                id="tempoAcesso"
                                label='Tempo de acesso'
                                placeholder={'Selecione'}
                                defaultOption={'Todos'}
                                value={bodyFilters?.tempoAcesso}
                                options={sortArrayByDirection(filterOptions?.temposDeAcesso)}
                                onChange={(value) => {
                                    setBodyFilters({
                                        property: 'tempoAcesso',
                                        value: value,
                                        putOnUrl: true,
                                    });
                                }}
                            />
                        </Grid>
                    </Grid>

                    <Grid container spacing={2} my={2}>
                        <Grid item lg={3} md={3} mt={0}>
                            <div>
                                <FormLabel id="rbutton-label">Tipo de usuário</FormLabel>
                                <RadioGroup
                                    value={tipoUsuarioRadio}
                                    onChange={handleChangeRadio}
                                    row
                                    aria-labelledby="rbutton-label"
                                    name="row-radio-buttons-group"
                                >
                                    <FormControlLabel value="todos" control={<Radio/>} label="Todos"/>
                                    <FormControlLabel value="cpf" control={<Radio/>} label="CPF"/>
                                    <FormControlLabel value="cnpj" control={<Radio/>} label="CNPJ"/>
                                </RadioGroup>
                            </div>
                        </Grid>

                        <Grid item lg={3} md={3}>
                            <SEFilterSelect
                                id={"mes"}
                                label='Mês'
                                defaultOption={'Todos'}
                                value={bodyFilters?.mes}
                                options={filterOptions?.meses}
                                onChange={(value) => {
                                    setBodyFilters({
                                        property: 'mes',
                                        value: value,
                                        putOnUrl: true,
                                    });
                                }}
                            />
                        </Grid>

                        <Grid item lg={3} md={3}>
                            <SEFilterSelect
                                id={"ano"}
                                label='Ano'
                                defaultOption={'Todos'}
                                value={bodyFilters?.ano}
                                options={sortArrayByDirection(filterOptions?.anos, 'DESC')}
                                onChange={(value) => {
                                    setBodyFilters({
                                        property: 'ano',
                                        value: value,
                                        putOnUrl: true,
                                    });
                                }}
                            />
                        </Grid>

                        <Grid item lg={3} md={3}>
                            <SEFilterSelect
                                customLabel={(value) => value.replaceAll('_', ' ')}
                                id={"usuariosQue"}
                                label='Usuários que'
                                defaultOption={'Todos'}
                                value={bodyFilters?.usuariosQue}
                                options={filterOptions?.usuariosQue}
                                onChange={(value) => {
                                    setBodyFilters({
                                        property: 'usuariosQue',
                                        value: value,
                                        putOnUrl: true,
                                    });
                                }}
                            />
                        </Grid>


                    </Grid>

                    <Grid container justifyContent={"end"} mb={2}>
                        <Grid item flexDirection='row' display="flex" alignItems="center">

                            <Button size={"small"} onClick={handleResetFilters} variant="outlined" startIcon={<RiFilterOffLine/>}
                            >
                                <SEText as="p" size={"small"} font="Mulish" weight="bold">Limpar filtros</SEText>
                            </Button>

                        </Grid>
                    </Grid>
                </ThemeProvider>
            </Grid>
            <Box sx={{marginTop: '12px'}}>
                <SETable
                    totalElements={data?.totalElements}
                    totalPages={data?.totalPages}
                    linesPerPageFilters={paginationFilters?.linesPerPage}
                    isLoading={isLoading}
                    data={data?.content}
                    page={paginationFilters?.page}
                    columns={tableColumns}
                    orderBy={paginationFilters?.orderBy}
                    direction={paginationFilters?.direction}
                    onOrderChange={handleChangeOrder}
                    onPagePreviousChange={handlePageChange}
                    onPageNextChange={handlePageChange}
                    onLinesPerPageChange={handleLinesPerPage}
                    tableHeader={(
                        <Grid container py={2} px={1} justifyContent="space-between" alignItems="center">
                            <Grid item md={3}>
                                <FormControl fullWidth>
                                    <TextField
                                        label="Buscar"
                                        value={bodyFilters?.oqueVoceProcura}
                                        onChange={handleSearchChange}
                                        variant="outlined"
                                        className={classes.textField}
                                        InputProps={{
                                            startAdornment: (
                                                <IconButton>
                                                    <RiSearchLine/>
                                                </IconButton>
                                            )
                                        }}

                                    />
                                </FormControl>
                            </Grid>

                            <Grid item container md={6} justifyContent="flex-end" alignItems="center">
                                <Grid item mr={2}>
                                    {!!data?.totalElements && (
                                        <SEText as="p" font="Mulish">
                                            Mostrando <strong>{data?.totalElements}</strong> {data?.totalElements === 1 ? 'resultado' : 'resultados'}
                                        </SEText>
                                    )}
                                </Grid>

                                <Grid item>
                                    <SEButtonExport isLoading={downloading} onClick={executeDownload}/>
                                </Grid>
                            </Grid>
                        </Grid>
                    )}

                />
            </Box>
        </React.Fragment>
    );
};

export default Report;